/** Words for the History tab: what an event did, and its changes as text diffs or short lines. */

import type { HistoryEvent } from '@/api/types';

const ACTIONS: Record<string, string> = {
  created: 'Created the asset',
  updated: 'Changed the fields',
  section_added: 'Added a section',
  section_updated: 'Changed a section',
  section_removed: 'Removed a section',
  published: 'Published',
  archived: 'Archived',
  unarchived: 'Unarchived (back to draft)',
  confirmed: 'Confirmed still accurate',
  material_added: 'Added a material',
  material_removed: 'Removed a material',
  expert_changed: 'Changed the experts',
  relation_changed: 'Changed the related assets',
  case_linked: 'Linked a success case',
  case_unlinked: 'Unlinked a success case',
  case_updated: 'Changed a linked success case',
  case_shareable_confirmed: 'Confirmed a success case as shareable',
  case_made_internal: 'Made a success case internal',
};

const FIELDS: Record<string, string> = {
  name: 'Name',
  kind: 'Kind',
  tagline: 'Tagline',
  summary: 'Summary',
  maturity: 'Maturity',
  home_country: 'Home country',
  status: 'Status',
  title: 'Title',
  body: 'Text',
  audience: 'Audience',
  position: 'Position',
  order: 'Section order',
  experts: 'Experts',
  industries: 'Industries',
  partners: 'Partners',
  countries: 'Countries',
  relations: 'Related assets',
  last_confirmed_at: 'Last confirmed',
};

/** Fields whose text deserves a word diff rather than one line. */
const TEXT_FIELDS = new Set(['body', 'summary', 'tagline', 'title', 'name']);

type Change = { before?: unknown; after?: unknown };

export interface TextChange {
  kind: 'text';
  field: string;
  before: string;
  after: string;
}
export interface LineChange {
  kind: 'line';
  field: string;
  text: string;
}

function asChange(value: unknown): Change {
  return value !== null && typeof value === 'object' ? (value as Change) : {};
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'None';
  if (Array.isArray(value)) return value.length === 0 ? 'None' : value.map(show).join(', ');
  if (typeof value === 'object') {
    const named = value as { display_name?: unknown; name?: unknown; user_id?: unknown };
    return String(named.display_name ?? named.name ?? named.user_id ?? JSON.stringify(value));
  }
  return String(value);
}

/** The section an event is about: its title on either side, else its key. */
function sectionName(changes: Record<string, unknown>): string | null {
  const title = asChange(changes.title);
  const key = asChange(changes.key);
  const name = title.after ?? title.before ?? key.after ?? key.before;
  return typeof name === 'string' ? name : null;
}

/** A `refined` event's own changes: the instruction (in its action text), the files read and how many changes. */
function refinedOf(changes: Record<string, unknown> | null): { instruction: string; files: string[]; count: number | null } {
  const after = (key: string) => (changes ? asChange(changes[key]).after : undefined);
  const instruction = after('instruction');
  const files = after('files');
  const count = after('changes');
  return {
    instruction: typeof instruction === 'string' ? instruction : '',
    files: Array.isArray(files) ? files.map(String) : [],
    count: typeof count === 'number' ? count : null,
  };
}

/** The lines under a `refined` event; its field and section changes are the events listed under it. */
function refinedLines(event: HistoryEvent): LineChange[] {
  const { files, count } = refinedOf(event.changes);
  const lines: LineChange[] = [];
  if (files.length > 0) lines.push({ kind: 'line', field: 'Files read', text: files.join(', ') });
  if (count !== null) lines.push({ kind: 'line', field: 'Changes applied', text: String(count) });
  return lines;
}

export function actionText(event: HistoryEvent): string {
  if (event.action === 'refined') {
    const { instruction } = refinedOf(event.changes);
    return instruction ? `Refined with AI: ${instruction}` : 'Refined with AI';
  }
  const base = ACTIONS[event.action] ?? event.action.replace(/_/g, ' ');
  const section = event.action.startsWith('section_') && event.changes ? sectionName(event.changes) : null;
  return section ? `${base}: ${section}` : base;
}

export function changeList(event: HistoryEvent): (TextChange | LineChange)[] {
  if (!event.changes) return [];
  if (event.action === 'refined') return refinedLines(event);
  return Object.entries(event.changes)
    // The key names the section (in the action text); an unchanged field says nothing.
    .filter(([field, raw]) => field !== 'key' && JSON.stringify(asChange(raw).before) !== JSON.stringify(asChange(raw).after))
    .map(([field, raw]) => {
      const { before, after } = asChange(raw);
      const label = FIELDS[field] ?? field;
      const textual = (before === null || typeof before === 'string') && (after === null || typeof after === 'string');
      if (TEXT_FIELDS.has(field) && textual) return { kind: 'text', field: label, before: (before as string | null) ?? '', after: (after as string | null) ?? '' };
      return { kind: 'line', field: label, text: `${show(before)} to ${show(after)}` };
    });
}
